export const PLAN_IDS = {
  FREE: 'free',
  BASIC: 'basic',
  PRO: 'pro',
};

export const TRIAL_DAYS = 30;
export const LAUNCH_DISCOUNT_RATE = 0.5;
export const LAUNCH_DISCOUNT_LIMIT = 100;

export const SUBSCRIPTION_PAGE_HEADER = {
  brand: 'Sigma Classroom',
  eyebrow: 'Subscription Launch',
  title: 'Start with a 30-day trial. Upgrade when your classroom is ready.',
  description:
    'Every account starts with trial access for classroom drafting. Basic unlocks saved canvases, PDFs, and sharing. Pro adds the assessment, eMarking, analysis, and learner workflows.',
  launchOfferTitle: 'Launch offer',
  launchOfferHighlight: '50% off yearly',
  launchOfferDescription: 'For the first 100 paid subscribers.',
};

export const SUBSCRIPTION_THEME = {
  pageShell: 'min-h-screen bg-gradient-to-b from-slate-50 via-white to-sigma-50 text-slate-950',
  heroShell:
    'rounded-[2rem] border border-sigma-200 bg-gradient-to-br from-sigma-600 via-sigma-700 to-sigma-900 p-6 text-white shadow-2xl shadow-sigma-900/20 sm:p-8',
  backButton:
    'inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white/85 transition hover:bg-white/20 hover:text-white',
  eyebrow:
    'mt-6 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-white',
  heroDescription: 'mt-4 max-w-2xl text-base leading-7 text-sigma-50/90',
  offerCard: 'min-w-[260px] rounded-3xl border border-white/20 bg-white/10 p-5 backdrop-blur-sm',
  offerLabel: 'flex items-center gap-2 text-sm font-semibold text-sigma-50',
  offerHighlight: 'mt-3 text-3xl font-black text-white',
  offerDescription: 'mt-2 text-sm leading-6 text-sigma-50/85',
  offerStatus: 'mt-3 text-sm font-medium text-white',
  errorBanner: 'mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700',
  planCardBase: 'flex h-full flex-col rounded-[1.75rem] border bg-white p-6 shadow-lg shadow-sigma-950/5',
  planCardAccent: {
    [PLAN_IDS.FREE]: 'border-slate-200',
    [PLAN_IDS.BASIC]: 'border-sigma-200 ring-1 ring-sigma-100',
    [PLAN_IDS.PRO]: 'border-amber-300/70 bg-gradient-to-b from-amber-50 to-white',
  },
  planButton: {
    disabled: 'cursor-not-allowed bg-slate-100 text-slate-400',
    [PLAN_IDS.FREE]: 'bg-white text-slate-950 hover:bg-slate-100 border border-slate-200',
    [PLAN_IDS.BASIC]: 'bg-sigma-600 text-white hover:bg-sigma-700',
    [PLAN_IDS.PRO]: 'bg-amber-300 text-slate-950 hover:bg-amber-200',
  },
  featureIcon: 'text-sigma-600',
  utilityCard: 'mt-8 rounded-3xl border border-sigma-100 bg-sigma-50/70 px-5 py-4 text-sm text-slate-700',
  utilityLinks: 'mt-6 text-sm text-slate-600',
  utilityLink: 'font-semibold text-sigma-700 underline decoration-sigma-200 underline-offset-4',
  checkoutOverlay: 'fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-sm',
  checkoutCard: 'w-full max-w-lg rounded-[2rem] border border-sigma-200 bg-white p-6 shadow-2xl',
  checkoutEyebrow: 'text-xs font-semibold uppercase tracking-[0.2em] text-sigma-600',
  checkoutClose:
    'rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-500 transition hover:bg-slate-100 hover:text-slate-900',
  checkoutSummary: 'mt-6 rounded-3xl border border-sigma-100 bg-sigma-50/70 p-5',
  checkoutRow: 'flex items-center justify-between text-sm text-slate-600',
  checkoutPrimary:
    'mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-sigma-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-sigma-700 disabled:cursor-not-allowed disabled:bg-slate-300',
};

export const SUBSCRIPTION_PLANS = {
  [PLAN_IDS.FREE]: {
    id: PLAN_IDS.FREE,
    name: '30-day Trial',
    annualPrice: 0,
    monthlyLabel: 'Trial',
    maxDocuments: 10,
    maxPagesPerDocument: 5,
    canSaveTools: false,
    canUploadPdf: false,
    canShare: false,
    hasAssessmentModule: false,
    hasManualMarking: false,
    hasAnalysis: false,
    hasLearnerManagement: false,
  },
  [PLAN_IDS.BASIC]: {
    id: PLAN_IDS.BASIC,
    name: 'Basic',
    annualPrice: 720,
    monthlyLabel: 'R90 pm',
    maxDocuments: null,
    maxPagesPerDocument: 20,
    canSaveTools: true,
    canUploadPdf: true,
    canShare: true,
    hasAssessmentModule: false,
    hasManualMarking: false,
    hasAnalysis: false,
    hasLearnerManagement: false,
  },
  [PLAN_IDS.PRO]: {
    id: PLAN_IDS.PRO,
    name: 'Pro',
    annualPrice: 1200,
    monthlyLabel: 'R180 pm',
    maxDocuments: null,
    maxPagesPerDocument: 100,
    canSaveTools: true,
    canUploadPdf: true,
    canShare: true,
    hasAssessmentModule: true,
    hasManualMarking: true,
    hasAnalysis: true,
    hasLearnerManagement: true,
  },
};

const PLAN_RANK = {
  [PLAN_IDS.FREE]: 0,
  [PLAN_IDS.BASIC]: 1,
  [PLAN_IDS.PRO]: 2,
};

const normalizePlanId = (planId) => (SUBSCRIPTION_PLANS[planId] ? planId : PLAN_IDS.FREE);

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const getTrialStartDate = (subscription = {}, userData = null) =>
  toDate(subscription.activatedAt) || toDate(userData?.createdAt) || new Date();

const buildTrialSubscription = (subscription = {}, userData = null) => {
  const activatedAt = getTrialStartDate(subscription, userData);
  const currentPeriodEnd = toDate(subscription.currentPeriodEnd) || addDays(activatedAt, TRIAL_DAYS);
  const isExpired = currentPeriodEnd.getTime() < Date.now();

  return {
    ...subscription,
    planId: PLAN_IDS.FREE,
    status: isExpired ? 'expired' : 'active',
    billingCycle: 'trial',
    activatedAt,
    currentPeriodEnd,
    trialDays: TRIAL_DAYS,
    trialDaysRemaining: Math.max(
      0,
      Math.ceil((currentPeriodEnd.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
    ),
    isTrial: true,
    launchDiscountApplied: false,
    subscriberNumber: null,
    amountPaid: 0,
  };
};

export const getUserSubscription = (userData = null) => {
  const subscription = userData?.subscription || {};
  const normalizedPlanId = normalizePlanId(subscription.planId || PLAN_IDS.FREE);
  const currentPeriodEnd = toDate(subscription.currentPeriodEnd);

  if (normalizedPlanId === PLAN_IDS.FREE) {
    return buildTrialSubscription(subscription, userData);
  }

  const isExpired =
    currentPeriodEnd instanceof Date &&
    currentPeriodEnd.getTime() < Date.now();
  const status = isExpired ? 'expired' : (subscription.status || 'inactive');

  if (status !== 'active') {
    return {
      ...subscription,
      planId: normalizedPlanId,
      status,
      currentPeriodEnd,
    };
  }

  return {
    ...subscription,
    planId: normalizedPlanId,
    status,
    currentPeriodEnd,
  };
};

export const getPendingSubscriptionRequest = (userData = null) => {
  const request = userData?.subscriptionRequest || {};
  const planId = normalizePlanId(request.planId || PLAN_IDS.FREE);
  const status = String(request.status || '').trim().toLowerCase();

  if (planId === PLAN_IDS.FREE || status !== 'pending') {
    return null;
  }

  return {
    ...request,
    planId,
    status: 'pending',
    requestedAt: toDate(request.requestedAt),
  };
};

export const getCurrentPlan = (userData = null) =>
  SUBSCRIPTION_PLANS[getUserSubscription(userData).planId] || SUBSCRIPTION_PLANS[PLAN_IDS.FREE];

export const hasPlanAccess = (userData, requiredPlanId = PLAN_IDS.FREE) => {
  const subscription = getUserSubscription(userData);
  const plan = SUBSCRIPTION_PLANS[subscription.planId] || SUBSCRIPTION_PLANS[PLAN_IDS.FREE];
  return subscription.status === 'active' && PLAN_RANK[plan.id] >= PLAN_RANK[requiredPlanId];
};

const getActivePlan = (userData) => {
  const subscription = getUserSubscription(userData);
  if (subscription.status !== 'active') return SUBSCRIPTION_PLANS[PLAN_IDS.FREE];
  return SUBSCRIPTION_PLANS[subscription.planId] || SUBSCRIPTION_PLANS[PLAN_IDS.FREE];
};

export const canSaveToolWork = (userData) => getActivePlan(userData).canSaveTools;
export const canUsePdfUploads = (userData) => getActivePlan(userData).canUploadPdf;
export const canShareContent = (userData) => getActivePlan(userData).canShare;
export const canUseAssessmentModule = (userData) => getActivePlan(userData).hasAssessmentModule;
export const canUseManualMarking = (userData) => getActivePlan(userData).hasManualMarking;
export const canUseAnalysis = (userData) => getActivePlan(userData).hasAnalysis;
export const canUseLearnerManagement = (userData) => getActivePlan(userData).hasLearnerManagement;

export const getDocumentLimits = (userData) => {
  const plan = getCurrentPlan(userData);
  return {
    maxDocuments: plan.maxDocuments,
    maxPagesPerDocument: plan.maxPagesPerDocument,
  };
};

export const getPlanPrice = (planId, { withLaunchDiscount = false } = {}) => {
  const plan = SUBSCRIPTION_PLANS[planId] || SUBSCRIPTION_PLANS[PLAN_IDS.FREE];
  if (!withLaunchDiscount || plan.annualPrice <= 0) return plan.annualPrice;
  return Math.round(plan.annualPrice * (1 - LAUNCH_DISCOUNT_RATE));
};

export const buildSubscriptionRecord = ({
  planId,
  launchDiscountApplied = false,
  subscriberNumber = null,
  activatedAt = new Date(),
  approvedByAdmin = false,
}) => {
  const plan = SUBSCRIPTION_PLANS[planId] || SUBSCRIPTION_PLANS[PLAN_IDS.FREE];
  if (plan.id === PLAN_IDS.FREE) {
    const currentPeriodEnd = addDays(activatedAt, TRIAL_DAYS);

    return {
      planId: PLAN_IDS.FREE,
      status: 'active',
      billingCycle: 'trial',
      amountPaid: 0,
      activatedAt,
      currentPeriodEnd,
      trialDays: TRIAL_DAYS,
      isTrial: true,
      launchDiscountApplied: false,
      subscriberNumber: null,
    };
  }

  if (!approvedByAdmin) {
    return buildSubscriptionRequestRecord({
      planId: plan.id,
      launchDiscountApplied,
      requestedAt: activatedAt,
    });
  }

  const currentPeriodEnd = new Date(activatedAt);
  currentPeriodEnd.setFullYear(currentPeriodEnd.getFullYear() + 1);

  return {
    planId: plan.id,
    status: 'active',
    billingCycle: 'yearly',
    amountPaid: getPlanPrice(plan.id, { withLaunchDiscount: launchDiscountApplied }),
    listPrice: plan.annualPrice,
    activatedAt,
    currentPeriodEnd,
    launchDiscountApplied,
    subscriberNumber,
  };
};

export const buildSubscriptionRequestRecord = ({
  planId,
  launchDiscountApplied = false,
  requestedAt = new Date(),
}) => {
  const plan = SUBSCRIPTION_PLANS[normalizePlanId(planId)];
  if (plan.id === PLAN_IDS.FREE) {
    return null;
  }

  return {
    planId: plan.id,
    status: 'pending',
    billingCycle: 'yearly',
    amountPaid: getPlanPrice(plan.id, { withLaunchDiscount: launchDiscountApplied }),
    listPrice: plan.annualPrice,
    launchDiscountApplied,
    requestedAt,
  };
};

export const evaluateDocumentSaveAccess = ({
  userData,
  existingDocumentId = null,
  documentCount = 0,
  nextPageCount = 1,
}) => {
  const subscription = getUserSubscription(userData);
  const plan = getCurrentPlan(userData);

  if (subscription.status !== 'active') {
    return {
      allowed: false,
      message:
        plan.id === PLAN_IDS.FREE
          ? 'Your 30-day trial has ended. Upgrade to Basic or Pro to continue saving documents.'
          : 'Your subscription is not active. Renew or upgrade to continue saving documents.',
    };
  }

  if (nextPageCount > plan.maxPagesPerDocument) {
    return {
      allowed: false,
      message:
        plan.id === PLAN_IDS.FREE
          ? 'Trial access includes documents with up to 5 pages. Upgrade to Basic for up to 20 pages or Pro for up to 100 pages per document.'
          : `The ${plan.name} plan supports documents with up to ${plan.maxPagesPerDocument} pages each.`,
    };
  }

  if (!existingDocumentId && plan.maxDocuments != null && documentCount >= plan.maxDocuments) {
    return {
      allowed: false,
      message: 'Trial access includes up to 10 saved documents. Upgrade to Basic or Pro for unlimited documents.',
    };
  }

  return { allowed: true, message: '' };
};

export const getToolSaveMessage = () =>
  'Saving tool canvases and slides requires Basic or Pro. Trial access still lets users work in the tools before they choose to save.';

export const getFeatureGateMessage = (requiredPlanId) => {
  if (requiredPlanId === PLAN_IDS.BASIC) {
    return 'This feature is available on Basic and Pro plans.';
  }
  if (requiredPlanId === PLAN_IDS.PRO) {
    return 'This feature is available on the Pro plan.';
  }
  return 'Upgrade your subscription to continue.';
};
